import { useState } from "react";
import { Link } from "react-router-dom";
import { publishApi } from "../api/client";
import type { SitePublication } from "../types";
import { useToast } from "../lib/toast";
import "./PublicationCard.css";

/** "in 23h" / "in 40m" — anonymous sites live for 24 hours. */
export function expiresIn(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return "expired";
  const minutes = Math.round(ms / 60000);
  return minutes >= 60 ? `in ${Math.round(minutes / 60)}h` : `in ${Math.max(1, minutes)}m`;
}

function destination(p: SitePublication): string {
  if (p.anonymous) return "Anonymous";
  if (p.account_type === "personal") return "here.now Home";
  return `${p.account_name ?? "Workspace"} workspace`;
}

/**
 * One published site: where it lives, the links here.now returned, and the
 * few lifecycle actions chatter owns (republish, claim, delete). Everything
 * else — access, names, folders — is a click away in here.now's own editor.
 */
export default function PublicationCard({
  publication: p,
  connected,
  showChatLink = false,
}: {
  publication: SitePublication;
  /** Whether the viewer has a here.now account connected (enables Claim). */
  connected: boolean;
  showChatLink?: boolean;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const live = p.status === "live";

  const run = async (label: string, action: () => Promise<unknown>, success?: string) => {
    setBusy(label);
    try {
      await action();
      if (success) toast.success(success);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select the link instead.");
    }
  };

  const statusTag = !live
    ? <span className={`tag ${p.status === "deleted" ? "tag-danger" : "tag-muted"}`}>{p.status === "deleted" ? "Deleted" : "Expired"}</span>
    : p.anonymous && p.expires_at
      ? <span className="tag tag-warning">Expires {expiresIn(p.expires_at)}</span>
      : <span className="tag tag-success">Live</span>;

  return (
    <article className="publication-card" aria-label={`Published site ${p.title || p.slug}`}>
      <header className="publication-card-head">
        <span className="publication-card-icon" aria-hidden>🌐</span>
        <div className="publication-card-heading">
          <div className="publication-card-title">{p.title || p.slug}</div>
          <div className="publication-card-meta">
            {destination(p)}
            {p.revision_sequence != null && <> · revision {p.revision_sequence}</>}
            {p.publish_count > 1 && <> · published {p.publish_count}×</>}
            {" · "}
            {new Date(p.updated_at).toLocaleString()}
          </div>
        </div>
        {statusTag}
      </header>

      {live && (
        <div className="publication-card-links">
          <a className="publication-card-url" href={p.primary_url} target="_blank" rel="noopener noreferrer">
            {p.primary_url.replace(/^https:\/\//, "").replace(/\/$/, "")}
          </a>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => void copy(p.primary_url)}>Copy</button>
          {p.domain_url && p.domain_url !== p.primary_url && (
            <a className="publication-card-url" href={p.domain_url} target="_blank" rel="noopener noreferrer">
              {p.domain_url.replace(/^https:\/\//, "").replace(/\/$/, "")}
            </a>
          )}
        </div>
      )}

      {live && p.claim_url && (
        <p className="publication-card-note">
          Anonymous sites disappear after 24 hours.{" "}
          {connected ? "Claim it into your here.now account to keep it." : (
            <>
              Keep it with the{" "}
              <a href={p.claim_url} target="_blank" rel="noopener noreferrer">claim link</a>
              {" "}— anyone holding that link can take the site, so don't share it.
            </>
          )}
        </p>
      )}

      {p.warnings.length > 0 && (
        <ul className="publication-card-warnings">
          {p.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}

      <div className="publication-card-actions">
        {live && p.editor_url && (
          <a className="btn btn-sm btn-primary" href={p.editor_url} target="_blank" rel="noopener noreferrer">
            Edit on here.now
          </a>
        )}
        {live && p.canvas_workspace_id && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy !== null}
            onClick={() => void run("republish", () => publishApi.republish(p.id), "Published the latest canvas revision")}
          >
            {busy === "republish" ? "Publishing…" : "Republish latest"}
          </button>
        )}
        {live && p.anonymous && connected && (
          <button
            type="button"
            className="btn btn-sm"
            disabled={busy !== null}
            onClick={() => void run("claim", () => publishApi.claim(p.id), "Site claimed into your here.now account")}
          >
            {busy === "claim" ? "Claiming…" : "Claim into my account"}
          </button>
        )}
        {showChatLink && p.chat_id && <Link className="btn btn-sm btn-ghost" to={`/chat/${p.chat_id}`}>Open chat</Link>}
        <span className="spacer" />
        {live && !p.anonymous && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busy !== null}
            onClick={() => {
              if (!window.confirm(`Delete ${p.primary_url} on here.now? The site goes offline for everyone. This cannot be undone.`)) return;
              void run("delete", () => publishApi.deleteSite(p.id), "Site deleted on here.now");
            }}
          >
            {busy === "delete" ? "Deleting…" : "Delete site"}
          </button>
        )}
        {(!live || p.anonymous) && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={busy !== null}
            onClick={() => {
              if (!window.confirm("Remove this record from chatter? The site itself is not affected.")) return;
              void run("forget", () => publishApi.forget(p.id));
            }}
          >
            Remove record
          </button>
        )}
      </div>
    </article>
  );
}
